import { DATE_RANGE_SCALE } from "./constants/filters";
import { STORE_TYPE_OPTIONS } from "./mockData";
import {
  DAIRY_BRAND_PROFILE_TOKEN,
  DO_GOOD_CANDLE_PROFILE_TOKEN,
  ELANA_GABRIELLE_PROFILE_TOKEN,
  GOOD_BOOK_PROFILE_TOKEN,
  KAARI_CO_PROFILE_TOKEN,
  KING_FLOYDS_PROFILE_TOKEN,
  OGGI_PROFILE_TOKEN,
  WOODSY_CRAFT_PROFILE_TOKEN,
  ZOTTER_CHOCOLATES_PROFILE_TOKEN,
  getActiveProfileToken,
  getP8ActivityMonthlyTotals,
  getP8MonthlyTotals,
  getP8OverviewTotals,
  getP8SegmentScale,
  getP8TableSegmentScale,
} from "./p8AdvertiserData";
import type {
  AdsDateRange,
  InsightsChartMetric,
  InsightsCustomerSegment,
  InsightsStoreTypeGrouping,
} from "./types";

export const INSIGHTS_STORE_TYPES = [...STORE_TYPE_OPTIONS, "Other"] as const;

/** Curated top-5 lines for the modeled shape. */
export const TOP_CHART_STORE_TYPES = [
  "Gift store",
  "Home decor store",
  "General store",
  "Apparel boutique",
  "Spa or salon",
] as const;

const NUM_TOP_CHART_LINES = TOP_CHART_STORE_TYPES.length;

const CHART_LINE_COLORS: Record<string, string> = {
  "Gift store": "#b3c5ff",
  "Home decor store": "#f47d3f",
  "General store": "#5b785e",
  "Apparel boutique": "#9b894e",
  "Spa or salon": "#c557aa",
  "Florist or garden store": "#7EB8DA",
  "Cafe or restaurant": "#B8A07E",
  "Kids or toy store": "#E8A87C",
  "Pet store": "#8FBC8F",
  "Grocery or liquor store": "#A0A0A0",
  "Fitness or yoga studio": "#C4A882",
  Bookstore: "#9BB5CE",
  "Furniture store": "#B8956B",
  Other: "#999999",
};

type StoreMetrics = {
  adSpend: number;
  adSales: number;
  impressions: number;
  clicks: number;
  conversions: number;
};

/** Relative store-type mix; scaled to P8 overview totals at runtime. */
const STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Florist or garden store": {
    adSpend: 2640,
    adSales: 9840,
    impressions: 98500,
    clicks: 3420,
    conversions: 28,
  },
  "Gift store": {
    adSpend: 4120,
    adSales: 18240,
    impressions: 182400,
    clicks: 6540,
    conversions: 48,
  },
  "Home decor store": {
    adSpend: 3890,
    adSales: 15680,
    impressions: 156200,
    clicks: 5210,
    conversions: 41,
  },
  "General store": {
    adSpend: 2110,
    adSales: 7420,
    impressions: 88300,
    clicks: 2890,
    conversions: 22,
  },
  "Apparel boutique": {
    adSpend: 1980,
    adSales: 6280,
    impressions: 72100,
    clicks: 2540,
    conversions: 19,
  },
  "Spa or salon": {
    adSpend: 1450,
    adSales: 4960,
    impressions: 54800,
    clicks: 1820,
    conversions: 14,
  },
  "Cafe or restaurant": {
    adSpend: 1200,
    adSales: 3800,
    impressions: 42000,
    clicks: 1400,
    conversions: 11,
  },
  "Kids or toy store": {
    adSpend: 980,
    adSales: 3100,
    impressions: 35000,
    clicks: 1180,
    conversions: 9,
  },
  "Pet store": {
    adSpend: 850,
    adSales: 2720,
    impressions: 30200,
    clicks: 1020,
    conversions: 8,
  },
  "Grocery or liquor store": {
    adSpend: 720,
    adSales: 2280,
    impressions: 26000,
    clicks: 880,
    conversions: 7,
  },
  "Fitness or yoga studio": {
    adSpend: 640,
    adSales: 2040,
    impressions: 22800,
    clicks: 760,
    conversions: 6,
  },
  Bookstore: {
    adSpend: 520,
    adSales: 1680,
    impressions: 18500,
    clicks: 620,
    conversions: 5,
  },
  "Furniture store": {
    adSpend: 480,
    adSales: 1520,
    impressions: 16800,
    clicks: 560,
    conversions: 4,
  },
  Other: {
    adSpend: 1100,
    adSales: 3500,
    impressions: 38000,
    clicks: 1280,
    conversions: 10,
  },
};

/**
 * Builds a store-type row for a brand whose real mix we have. Only the
 * conversion count and order dollars come from Snowflake; impressions, clicks,
 * and spend are held proportional to conversions (~$76 CPA), which is enough
 * because `scaleShapeToP8` renormalizes every field to the profile's own real
 * totals — the shape only supplies relative weights.
 */
function realStoreMetrics(conversions: number, adSales: number): StoreMetrics {
  return {
    conversions,
    adSales,
    impressions: conversions * 1000,
    clicks: conversions * 15,
    adSpend: conversions * 76,
  };
}

/**
 * The Good Book Company — REAL store-type mix of ad-acquired customers, from
 * ADS.BRAND_ORDERS joined to CANONICAL.RETAILERS.store_type_prediction (brand
 * 815063, ad-click-attributed orders). Weights are distinct customers and
 * post-discount order dollars per store type. Snowflake store-type codes are
 * mapped to the app's labels (clothing* → Apparel boutique; cafe + restaurant →
 * Cafe or restaurant; civic/events/services/jewelry → Other).
 */
const GOOD_BOOK_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  Bookstore: realStoreMetrics(9, 2737),
  "Gift store": realStoreMetrics(7, 1936),
  "Apparel boutique": realStoreMetrics(4, 1231),
  "Home decor store": realStoreMetrics(3, 425),
  "Cafe or restaurant": realStoreMetrics(2, 273),
  "Florist or garden store": realStoreMetrics(1, 247),
  Other: realStoreMetrics(6, 1685),
  "General store": realStoreMetrics(0, 0),
  "Fitness or yoga studio": realStoreMetrics(0, 0),
  "Furniture store": realStoreMetrics(0, 0),
  "Grocery or liquor store": realStoreMetrics(0, 0),
  "Kids or toy store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/**
 * King Floyd's — REAL store-type mix, from
 * ADS_SNAPSHOTS.MANAGER_METRICS_BRAND_ORDERS (advertiser 6775715154222277513,
 * both campaigns, snapshot 2026-08-18): 33 ad-attributed orders totalling
 * $8,393. Weights are orders and order dollars per store type; impressions,
 * clicks, and spend stay proportional to orders. Snowflake labels map to the
 * app's ("Grocery / Liquor Store" → "Grocery or liquor store", and so on).
 * A cocktail-bitters brand sells to bars and bottle shops, which is why this
 * looks nothing like the modeled gift/home-decor mix.
 */
const KING_FLOYDS_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Grocery or liquor store": realStoreMetrics(10, 2942),
  "Cafe or restaurant": realStoreMetrics(9, 2173),
  "Gift store": realStoreMetrics(6, 1833),
  "Furniture store": realStoreMetrics(3, 598),
  "General store": realStoreMetrics(2, 226),
  Other: realStoreMetrics(2, 456),
  "Home decor store": realStoreMetrics(1, 165),
  "Apparel boutique": realStoreMetrics(0, 0),
  Bookstore: realStoreMetrics(0, 0),
  "Fitness or yoga studio": realStoreMetrics(0, 0),
  "Florist or garden store": realStoreMetrics(0, 0),
  "Kids or toy store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/**
 * Oggi — REAL store-type mix, same source (advertiser 4748191580351722375):
 * 54 ad-attributed orders totalling $17,440.
 */
const OGGI_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Home decor store": realStoreMetrics(13, 6344),
  "Cafe or restaurant": realStoreMetrics(10, 2774),
  "Gift store": realStoreMetrics(9, 1777),
  "General store": realStoreMetrics(7, 2608),
  "Grocery or liquor store": realStoreMetrics(6, 1754),
  Other: realStoreMetrics(3, 849),
  "Apparel boutique": realStoreMetrics(2, 371),
  "Furniture store": realStoreMetrics(2, 573),
  "Kids or toy store": realStoreMetrics(1, 173),
  "Pet store": realStoreMetrics(1, 217),
  Bookstore: realStoreMetrics(0, 0),
  "Fitness or yoga studio": realStoreMetrics(0, 0),
  "Florist or garden store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/**
 * Elana Gabrielle — REAL store-type mix, same source
 * (advertiser 9095933244058406210): 135 ad-attributed orders totalling $32,728.
 * The widest spread of any of these brands — printed textiles and paper goods
 * sell into home decor, apparel, and gift shops in roughly equal measure.
 */
const ELANA_GABRIELLE_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Home decor store": realStoreMetrics(36, 9946),
  "Gift store": realStoreMetrics(25, 6354),
  "Apparel boutique": realStoreMetrics(24, 5332),
  "Florist or garden store": realStoreMetrics(12, 2773),
  "General store": realStoreMetrics(12, 2438),
  "Cafe or restaurant": realStoreMetrics(10, 1617),
  Other: realStoreMetrics(7, 1323),
  "Grocery or liquor store": realStoreMetrics(5, 1126),
  "Furniture store": realStoreMetrics(2, 1055),
  Bookstore: realStoreMetrics(1, 505),
  "Fitness or yoga studio": realStoreMetrics(1, 260),
  "Kids or toy store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/**
 * Kaari & Co. — REAL store-type mix, same source
 * (advertiser 1756243082779176848): 101 ad-attributed orders totalling $23,338.
 * Art prints and stationery, so gift stores lead by a wide margin and bookstores
 * place higher than they do for anyone else here.
 */
const KAARI_CO_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Gift store": realStoreMetrics(29, 8168),
  "General store": realStoreMetrics(13, 3546),
  Bookstore: realStoreMetrics(13, 2559),
  "Apparel boutique": realStoreMetrics(13, 2916),
  "Cafe or restaurant": realStoreMetrics(10, 1666),
  "Florist or garden store": realStoreMetrics(9, 1657),
  "Grocery or liquor store": realStoreMetrics(7, 1446),
  "Home decor store": realStoreMetrics(5, 1017),
  Other: realStoreMetrics(1, 165),
  "Fitness or yoga studio": realStoreMetrics(1, 198),
  "Furniture store": realStoreMetrics(0, 0),
  "Kids or toy store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/**
 * The Woodsy Craft Co. — REAL store-type mix, same source
 * (advertiser -7828252857858171118): 46 ad-attributed orders totalling $12,010.
 * The most concentrated mix of the set — half the orders are gift stores, and
 * the long tail is a single order each, which is what 17 lifetime customers
 * looks like.
 */
const WOODSY_CRAFT_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Gift store": realStoreMetrics(23, 6062),
  Bookstore: realStoreMetrics(10, 2892),
  "Home decor store": realStoreMetrics(6, 1360),
  "Fitness or yoga studio": realStoreMetrics(2, 748),
  "Florist or garden store": realStoreMetrics(1, 263),
  "General store": realStoreMetrics(1, 248),
  "Spa or salon": realStoreMetrics(1, 167),
  Other: realStoreMetrics(1, 150),
  "Apparel boutique": realStoreMetrics(1, 120),
  "Cafe or restaurant": realStoreMetrics(0, 0),
  "Furniture store": realStoreMetrics(0, 0),
  "Grocery or liquor store": realStoreMetrics(0, 0),
  "Kids or toy store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
};

/**
 * Zotter Chocolates — REAL store-type mix, same source
 * (advertiser -2425147271178345382): 182 ad-attributed orders totalling $64,259
 * across both campaigns. The only food mix here that isn't a bar program —
 * chocolate sells to gift shops and grocers first, and cafes buy it to resell
 * rather than to pour, so the tail runs through bookstores and general stores
 * instead of restaurants.
 */
const ZOTTER_CHOCOLATES_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Gift store": realStoreMetrics(72, 19229),
  "Grocery or liquor store": realStoreMetrics(42, 16580),
  "Cafe or restaurant": realStoreMetrics(16, 4226),
  "General store": realStoreMetrics(14, 7956),
  Bookstore: realStoreMetrics(11, 5463),
  "Home decor store": realStoreMetrics(9, 2362),
  "Florist or garden store": realStoreMetrics(6, 1109),
  Other: realStoreMetrics(5, 3035),
  "Apparel boutique": realStoreMetrics(3, 1921),
  "Spa or salon": realStoreMetrics(2, 395),
  "Kids or toy store": realStoreMetrics(1, 1605),
  "Fitness or yoga studio": realStoreMetrics(1, 378),
  "Furniture store": realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
};

/**
 * Dairy Brand — REAL store-type mix, same source
 * (advertiser 8749774711315878893): 3,166 ad-attributed orders totalling
 * $544,484 across both campaigns, an order of magnitude more than any other
 * variant. The only mix with no empty store type — at this volume even pet
 * stores and bookstores have bought. Grocery leads by 2:1 over anything else,
 * and cafes and restaurants place second, which is the wholesale-cheese shape:
 * it sells to the people who resell it by the pound and the people who cook it.
 */
const DAIRY_BRAND_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Grocery or liquor store": realStoreMetrics(1258, 225538),
  "Cafe or restaurant": realStoreMetrics(550, 76484),
  "General store": realStoreMetrics(542, 104221),
  "Gift store": realStoreMetrics(346, 62825),
  "Home decor store": realStoreMetrics(134, 24413),
  "Apparel boutique": realStoreMetrics(88, 10541),
  "Florist or garden store": realStoreMetrics(77, 12159),
  Other: realStoreMetrics(57, 8611),
  "Kids or toy store": realStoreMetrics(39, 7518),
  "Pet store": realStoreMetrics(38, 6803),
  "Spa or salon": realStoreMetrics(20, 2058),
  "Fitness or yoga studio": realStoreMetrics(8, 894),
  "Furniture store": realStoreMetrics(5, 1284),
  Bookstore: realStoreMetrics(4, 1136),
};

/**
 * Do Good Candle Studio — REAL store-type mix, same source
 * (advertiser 3706895363597287505): 3 ad-attributed orders totalling $1,167 from
 * 2 retailers. The smallest mix here by two orders of magnitude, and the only one
 * where twelve of the fourteen store types are empty — a home decor shop that
 * ordered twice and a gift shop that ordered once is the brand's entire
 * ads-acquired customer base.
 */
const DO_GOOD_CANDLE_STORE_TYPE_SHAPE: Record<string, StoreMetrics> = {
  "Home decor store": realStoreMetrics(2, 677),
  "Gift store": realStoreMetrics(1, 491),
  "Apparel boutique": realStoreMetrics(0, 0),
  Bookstore: realStoreMetrics(0, 0),
  "Cafe or restaurant": realStoreMetrics(0, 0),
  "Fitness or yoga studio": realStoreMetrics(0, 0),
  "Florist or garden store": realStoreMetrics(0, 0),
  "Furniture store": realStoreMetrics(0, 0),
  "General store": realStoreMetrics(0, 0),
  "Grocery or liquor store": realStoreMetrics(0, 0),
  "Kids or toy store": realStoreMetrics(0, 0),
  Other: realStoreMetrics(0, 0),
  "Pet store": realStoreMetrics(0, 0),
  "Spa or salon": realStoreMetrics(0, 0),
};

/** Real mixes where we have them; the modeled shape covers everyone else. */
const REAL_STORE_TYPE_SHAPES: Record<string, Record<string, StoreMetrics>> = {
  [GOOD_BOOK_PROFILE_TOKEN]: GOOD_BOOK_STORE_TYPE_SHAPE,
  [KING_FLOYDS_PROFILE_TOKEN]: KING_FLOYDS_STORE_TYPE_SHAPE,
  [OGGI_PROFILE_TOKEN]: OGGI_STORE_TYPE_SHAPE,
  [ELANA_GABRIELLE_PROFILE_TOKEN]: ELANA_GABRIELLE_STORE_TYPE_SHAPE,
  [KAARI_CO_PROFILE_TOKEN]: KAARI_CO_STORE_TYPE_SHAPE,
  [WOODSY_CRAFT_PROFILE_TOKEN]: WOODSY_CRAFT_STORE_TYPE_SHAPE,
  [ZOTTER_CHOCOLATES_PROFILE_TOKEN]: ZOTTER_CHOCOLATES_STORE_TYPE_SHAPE,
  [DAIRY_BRAND_PROFILE_TOKEN]: DAIRY_BRAND_STORE_TYPE_SHAPE,
  [DO_GOOD_CANDLE_PROFILE_TOKEN]: DO_GOOD_CANDLE_STORE_TYPE_SHAPE,
};

function getStoreTypeShape(): Record<string, StoreMetrics> {
  return REAL_STORE_TYPE_SHAPES[getActiveProfileToken()] ?? STORE_TYPE_SHAPE;
}

/**
 * The store types the chart plots. Profiles with a real mix get their actual
 * top five by clicks, then impressions, matching how the table below it is
 * ranked, so a store type can't lead the table without appearing in the chart.
 * The modeled shape keeps its curated list. "Other" never gets a line — it's a
 * catch-all, not a store type a brand can act on.
 *
 * In today's real shapes clicks are a fixed multiple of conversions, so this
 * picks the same five either way; it's the ranking the table states, and it
 * stays aligned once per-store-type clicks come from Snowflake directly.
 */
function getTopChartStoreTypes(): string[] {
  const shape = REAL_STORE_TYPE_SHAPES[getActiveProfileToken()];
  if (!shape) {
    return [...TOP_CHART_STORE_TYPES];
  }

  return INSIGHTS_STORE_TYPES.filter((storeType) => storeType !== "Other")
    .slice()
    .sort(
      (left, right) =>
        (shape[right]?.clicks ?? 0) - (shape[left]?.clicks ?? 0) ||
        (shape[right]?.impressions ?? 0) - (shape[left]?.impressions ?? 0) ||
        left.localeCompare(right)
    )
    .slice(0, NUM_TOP_CHART_LINES);
}

/**
 * Draws a store type for a single customer row, weighted by the active
 * profile's mix, so the customer list reads like the store-type breakdown
 * instead of sampling all fourteen types uniformly. `random` is a 0–1 value the
 * caller has already seeded.
 */
export function pickWeightedStoreType(random: number): string {
  const shape = getStoreTypeShape();
  const total = sumShape(shape, "conversions");
  if (total <= 0) {
    return INSIGHTS_STORE_TYPES[0];
  }

  let remaining = random * total;
  for (const storeType of INSIGHTS_STORE_TYPES) {
    remaining -= shape[storeType]?.conversions ?? 0;
    if (remaining < 0) {
      return storeType;
    }
  }
  return INSIGHTS_STORE_TYPES[0];
}

function sumShape(
  shape: Record<string, StoreMetrics>,
  field: keyof StoreMetrics
): number {
  return INSIGHTS_STORE_TYPES.reduce(
    (total, storeType) => total + (shape[storeType]?.[field] ?? 0),
    0
  );
}

function scaleShapeToP8(): Record<string, StoreMetrics> {
  const p8 = getP8OverviewTotals();
  const storeTypeShape = getStoreTypeShape();
  const shapeTotals = {
    adSpend: sumShape(storeTypeShape, "adSpend"),
    adSales: sumShape(storeTypeShape, "adSales"),
    impressions: sumShape(storeTypeShape, "impressions"),
    clicks: sumShape(storeTypeShape, "clicks"),
    conversions: sumShape(storeTypeShape, "conversions"),
  };

  const scaled: Record<string, StoreMetrics> = {};
  for (const storeType of INSIGHTS_STORE_TYPES) {
    const shape = storeTypeShape[storeType];
    scaled[storeType] = {
      adSpend: Math.round((shape.adSpend / shapeTotals.adSpend) * p8.adSpend),
      adSales: Math.round((shape.adSales / shapeTotals.adSales) * p8.adSales),
      impressions: Math.round(
        (shape.impressions / shapeTotals.impressions) * p8.impressions
      ),
      clicks: Math.round((shape.clicks / shapeTotals.clicks) * p8.clicks),
      conversions: Math.round(
        (shape.conversions / shapeTotals.conversions) * p8.conversions
      ),
    };
  }
  return scaled;
}

let cachedScaledStoreMetrics: Record<string, StoreMetrics> | null = null;
let cachedScaledStoreMetricsProfileToken: string | null = null;

function getScaledStoreMetrics(): Record<string, StoreMetrics> {
  const profileToken = getActiveProfileToken();
  if (
    !cachedScaledStoreMetrics ||
    cachedScaledStoreMetricsProfileToken !== profileToken
  ) {
    cachedScaledStoreMetrics = scaleShapeToP8();
    cachedScaledStoreMetricsProfileToken = profileToken;
  }
  return cachedScaledStoreMetrics;
}

function seedRand(n: number) {
  const x = Math.sin(n * 9301 + 49297) * 10000;
  return x - Math.floor(x);
}

function getMetricValue(
  metrics: StoreMetrics,
  metric: InsightsChartMetric
): number {
  if (metric === "ad-sales") return metrics.adSales;
  if (metric === "impressions") return metrics.impressions;
  if (metric === "clicks") return metrics.clicks;
  return metrics.conversions;
}

function metricsForLine(
  lineKey: string,
  segment: InsightsCustomerSegment
): StoreMetrics {
  const scaled = getScaledStoreMetrics();
  const base = scaled[lineKey];

  return {
    adSpend: Math.round(
      base.adSpend * getP8TableSegmentScale(segment, "adSpend")
    ),
    adSales: Math.round(
      base.adSales * getP8TableSegmentScale(segment, "adSales")
    ),
    impressions: Math.round(
      base.impressions * getP8TableSegmentScale(segment, "impressions")
    ),
    clicks: Math.round(base.clicks * getP8TableSegmentScale(segment, "clicks")),
    conversions: Math.round(
      base.conversions * getP8TableSegmentScale(segment, "conversions")
    ),
  };
}

function resolveChartLines(
  grouping: InsightsStoreTypeGrouping,
  selectedStoreTypes: string[],
  hasBoosting: boolean
): string[] {
  if (!hasBoosting || grouping === "top") {
    return getTopChartStoreTypes();
  }
  if (grouping === "preferred") {
    return selectedStoreTypes.length > 0
      ? selectedStoreTypes
      : getTopChartStoreTypes().slice(0, 3);
  }
  const preferredSet = new Set(selectedStoreTypes);
  return INSIGHTS_STORE_TYPES.filter((type) => !preferredSet.has(type)).slice(
    0,
    6
  );
}

function monthlyMetricValue(
  month: P8MonthlyTotals,
  metric: InsightsChartMetric,
  segment: InsightsCustomerSegment
): number {
  const segmentScale = getP8SegmentScale(segment, metric);
  if (metric === "ad-sales") {
    return Math.round(month.adSales * segmentScale);
  }
  if (metric === "impressions") {
    return Math.round(month.impressions * segmentScale);
  }
  if (metric === "clicks") {
    return Math.round(month.clicks * segmentScale);
  }
  return Math.round(month.conversions * segmentScale);
}

type P8MonthlyTotals = ReturnType<typeof getP8MonthlyTotals>[number];

export type ChartLineConfig = {
  key: string;
  label: string;
  color: string;
};

export function getChartLineData(
  segment: InsightsCustomerSegment,
  grouping: InsightsStoreTypeGrouping,
  metric: InsightsChartMetric,
  selectedStoreTypes: string[],
  hasBoosting: boolean
): { data: Record<string, string | number>[]; lines: ChartLineConfig[] } {
  const lineKeys = resolveChartLines(grouping, selectedStoreTypes, hasBoosting);
  const lines: ChartLineConfig[] = lineKeys.map((key) => ({
    key,
    label: key,
    color: CHART_LINE_COLORS[key] ?? "#333333",
  }));

  const lineWeights = lineKeys.map((lineKey) =>
    getMetricValue(metricsForLine(lineKey, segment), metric)
  );
  const weightSum = lineWeights.reduce((sum, weight) => sum + weight, 0) || 1;
  const monthlyTotals = getP8MonthlyTotals();

  const data = monthlyTotals.map((month, monthIdx) => {
    const monthTotal = monthlyMetricValue(month, metric, segment);
    const point: Record<string, string | number> = { month: month.month };

    for (let index = 0; index < lineKeys.length; index += 1) {
      const lineKey = lineKeys[index];
      const share = lineWeights[index] / weightSum;
      const wobble = 0.9 + seedRand(monthIdx * 17 + lineKey.length * 3) * 0.2;
      point[lineKey] = Math.max(0, Math.round(monthTotal * share * wobble));
    }

    return point;
  });

  return { data, lines };
}

/**
 * Chart data for every store type, with a fixed denominator so each line's
 * absolute value is stable regardless of which lines are toggled on. Also
 * returns store-type keys ranked by the selected metric (used to pick the
 * default top-5 selection and to sort selected lines to the top of the legend).
 */
export function getAllStoreTypeChartData(
  segment: InsightsCustomerSegment,
  metric: InsightsChartMetric
): {
  data: Record<string, string | number>[];
  lines: ChartLineConfig[];
  rankedKeys: string[];
} {
  const lineKeys = [...INSIGHTS_STORE_TYPES];
  const lines: ChartLineConfig[] = lineKeys.map((key) => ({
    key,
    label: key,
    color: CHART_LINE_COLORS[key] ?? "#333333",
  }));

  const lineWeights = lineKeys.map((lineKey) =>
    getMetricValue(metricsForLine(lineKey, segment), metric)
  );
  const weightSum = lineWeights.reduce((sum, weight) => sum + weight, 0) || 1;
  const monthlyTotals = getP8ActivityMonthlyTotals();

  const data = monthlyTotals.map((month, monthIdx) => {
    const monthTotal = monthlyMetricValue(month, metric, segment);
    const point: Record<string, string | number> = { month: month.month };

    for (let index = 0; index < lineKeys.length; index += 1) {
      const lineKey = lineKeys[index];
      const share = lineWeights[index] / weightSum;
      const wobble = 0.9 + seedRand(monthIdx * 17 + lineKey.length * 3) * 0.2;
      point[lineKey] = Math.max(0, Math.round(monthTotal * share * wobble));
    }

    return point;
  });

  const rankedKeys = lineKeys
    .map((key, index) => ({ key, weight: lineWeights[index] }))
    .sort((left, right) => right.weight - left.weight)
    .map((entry) => entry.key);

  return { data, lines, rankedKeys };
}

const Y_AXIS_NICE_FACTORS = [
  1, 1.2, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10,
] as const;

function niceCeiling(value: number): number {
  if (value <= 0) {
    return 1;
  }

  const padded = value * 1.05;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const normalized = padded / magnitude;
  const niceUnit =
    Y_AXIS_NICE_FACTORS.find((factor) => factor >= normalized) ?? 10;

  return niceUnit * magnitude;
}

export function computeChartYAxisMax(
  data: Record<string, string | number>[],
  lineKeys: string[]
): number {
  let max = 0;
  for (const row of data) {
    for (const key of lineKeys) {
      const value = Number(row[key]);
      if (!Number.isNaN(value) && value > max) {
        max = value;
      }
    }
  }
  return niceCeiling(max);
}

export function getChartYAxisFormatter(metric: InsightsChartMetric) {
  if (metric === "ad-sales") {
    return (v: number) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`);
  }
  if (metric === "impressions") {
    return (v: number) =>
      v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v));
  }
  return (v: number) => String(Math.round(v));
}

export type InsightsTableRow = {
  storeType: string;
  adSpend: number;
  adSpendPct: number;
  adSales: number;
  adSalesPct: number;
  impressions: number;
  clicks: number;
  conversions: number;
  roas: number;
};

export function getInsightsTableRows(
  timeRange: AdsDateRange,
  segment: InsightsCustomerSegment,
  selectedStoreTypes: string[],
  hasBoosting: boolean
): InsightsTableRow[] {
  const timeScale = DATE_RANGE_SCALE[timeRange];
  const preferredSet = new Set(selectedStoreTypes);
  const scaled = getScaledStoreMetrics();

  const rows: InsightsTableRow[] = INSIGHTS_STORE_TYPES.map((storeType) => {
    const base = scaled[storeType];
    const adSpend = Math.round(
      base.adSpend * getP8TableSegmentScale(segment, "adSpend") * timeScale
    );
    const adSales = Math.round(
      base.adSales * getP8TableSegmentScale(segment, "adSales") * timeScale
    );
    return {
      storeType,
      adSpend,
      adSpendPct: 0,
      adSales,
      adSalesPct: 0,
      impressions: Math.round(
        base.impressions *
          getP8TableSegmentScale(segment, "impressions") *
          timeScale
      ),
      clicks: Math.round(
        base.clicks * getP8TableSegmentScale(segment, "clicks") * timeScale
      ),
      conversions: Math.round(
        base.conversions *
          getP8TableSegmentScale(segment, "conversions") *
          timeScale
      ),
      roas: adSpend > 0 ? adSales / adSpend : 0,
    };
  });

  const totalSpend = rows.reduce((sum, row) => sum + row.adSpend, 0);
  const totalSales = rows.reduce((sum, row) => sum + row.adSales, 0);
  for (const row of rows) {
    row.adSpendPct = totalSpend > 0 ? (row.adSpend / totalSpend) * 100 : 0;
    row.adSalesPct = totalSales > 0 ? (row.adSales / totalSales) * 100 : 0;
  }

  rows.sort((a, b) => b.adSales - a.adSales);

  if (hasBoosting && preferredSet.size > 0) {
    const preferred = rows
      .filter((row) => preferredSet.has(row.storeType))
      .sort((a, b) => b.adSales - a.adSales);
    const rest = rows
      .filter((row) => !preferredSet.has(row.storeType))
      .sort((a, b) => b.adSales - a.adSales);
    return [...preferred, ...rest];
  }

  return rows;
}

export function formatCurrency(n: number): string {
  return `$${n.toLocaleString("en-US")}`;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

export function formatPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

export function formatRoas(n: number): string {
  return `${n.toFixed(1)}x`;
}
